import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { defaultCursorBuilder, defaultSelectionBuilder, ySyncPluginKey, ySyncPlugin, yCursorPlugin, defaultDeleteFilter, yUndoPlugin, yUndoPluginKey, undo, redo } from 'y-prosemirror';
import { UndoManager } from 'yjs';
import { extension, invariant, ErrorConstant, ExtensionPriority, command, keyBinding, NamedShortcut, PlainExtension, isFunction, isEmptyObject, nonChainable, convertCommand } from '@remirror/core';
import { ExtensionHistoryMessages } from '@remirror/messages';

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
var YjsExtension = (_dec = extension({
  defaultOptions: {
    getProvider: () => {
      process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.EXTENSION,
        message: 'You must provide a YJS Provider to the `YjsExtension`.'
      }) : invariant(false) ;
    },
    destroyProvider: defaultDestroyProvider,
    syncPluginOptions: undefined,
    cursorBuilder: defaultCursorBuilder,
    selectionBuilder: defaultSelectionBuilder,
    cursorStateField: 'cursor',
    getSelection: state => state.selection,
    disableUndo: false,
    protectedNodes: new Set('paragraph'),
    trackedOrigins: []
  },
  staticKeys: ['disableUndo', 'protectedNodes', 'trackedOrigins'],
  defaultPriority: ExtensionPriority.High
}), _dec2 = command({
  disableChaining: true,
  description: _ref => {
    var t = _ref.t;
    return t(ExtensionHistoryMessages.UNDO_DESCRIPTION);
  },
  label: _ref2 => {
    var t = _ref2.t;
    return t(ExtensionHistoryMessages.UNDO_LABEL);
  },
  icon: 'arrowGoBackFill'
}), _dec3 = command({
  disableChaining: true,
  description: _ref3 => {
    var t = _ref3.t;
    return t(ExtensionHistoryMessages.REDO_DESCRIPTION);
  },
  label: _ref4 => {
    var t = _ref4.t;
    return t(ExtensionHistoryMessages.REDO_LABEL);
  },
  icon: 'arrowGoForwardFill'
}), _dec4 = keyBinding({
  shortcut: NamedShortcut.Undo,
  command: 'yUndo'
}), _dec5 = keyBinding({
  shortcut: NamedShortcut.Redo,
  command: 'yRedo'
}), _dec(_class = (_class2 = class YjsExtension extends PlainExtension {
  get name() {
    return 'yjs';
  }

  /**
   * The provider that is being used for the editor.
   */
  get provider() {
    var _this$_provider;

    var getProvider = this.options.getProvider;
    return (_this$_provider = this._provider) !== null && _this$_provider !== void 0 ? _this$_provider : this._provider = getLazyValue(getProvider);
  }

  getBinding() {
    var state = this.store.getState();

    var _ySyncPluginKey$getSt = ySyncPluginKey.getState(state),
        binding = _ySyncPluginKey$getSt.binding;

    return binding;
  }
  /**
   * Create the yjs plugins.
   */


  createExternalPlugins() {
    var _this$options = this.options,
        syncPluginOptions = _this$options.syncPluginOptions,
        cursorBuilder = _this$options.cursorBuilder,
        getSelection = _this$options.getSelection,
        cursorStateField = _this$options.cursorStateField,
        disableUndo = _this$options.disableUndo,
        protectedNodes = _this$options.protectedNodes,
        trackedOrigins = _this$options.trackedOrigins,
        selectionBuilder = _this$options.selectionBuilder;
    var yDoc = this.provider.doc;
    var type = yDoc.getXmlFragment('prosemirror');
    var plugins = [ySyncPlugin(type, syncPluginOptions), yCursorPlugin(this.provider.awareness, {
      cursorBuilder,
      getSelection,
      selectionBuilder
    }, cursorStateField)];

    if (!disableUndo) {
      var undoManager = new UndoManager(type, {
        trackedOrigins: new Set([ySyncPluginKey, ...trackedOrigins]),
        deleteFilter: item => defaultDeleteFilter(item, protectedNodes)
      });
      plugins.push(yUndoPlugin({
        undoManager
      }));
    }

    return plugins;
  }
  /**
   * This managers the updates of the collaboration provider.
   */


  onSetOptions(props) {
    var changes = props.changes,
        pickChanged = props.pickChanged;
    var changedPluginOptions = pickChanged(['cursorBuilder', 'cursorStateField', 'getProvider', 'getSelection', 'syncPluginOptions']);

    if (changes.getProvider.changed) {
      this._provider = undefined;
      var previousProvider = getLazyValue(changes.getProvider.previousValue); // Check whether the values have changed.

      if (changes.destroyProvider.changed) {
        var _changes$destroyProvi, _changes$destroyProvi2;

        (_changes$destroyProvi = (_changes$destroyProvi2 = changes.destroyProvider).previousValue) === null || _changes$destroyProvi === void 0 ? void 0 : _changes$destroyProvi.call(_changes$destroyProvi2, previousProvider);
      } else {
        this.options.destroyProvider(previousProvider);
      }
    }

    if (!isEmptyObject(changedPluginOptions)) {
      this.store.updateExtensionPlugins(this);
    }
  }
  /**
   * Remove the provider from the manager.
   */


  onDestroy() {
    if (!this._provider) {
      return;
    }

    this.options.destroyProvider(this._provider);
    this._provider = undefined;
  }
  /**
   * Undo that last Yjs transaction(s)
   *
   * This command does **not** support chaining.
   * This command is a no-op and always returns `false` when the `disableUndo` option is set.
   */


  yUndo() {
    return nonChainable(props => {
      if (this.options.disableUndo) {
        return false;
      }

      var state = props.state,
          dispatch = props.dispatch;
      var undoManager = yUndoPluginKey.getState(state).undoManager;

      if (undoManager.undoStack.length === 0) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      return convertCommand(undo)(props);
    });
  }
  /**
   * Redo the last transaction undone with a previous `yUndo` command.
   *
   * This command does **not** support chaining.
   * This command is a no-op and always returns `false` when the `disableUndo` option is set.
   */


  yRedo() {
    return nonChainable(props => {
      if (this.options.disableUndo) {
        return false;
      }

      var state = props.state,
          dispatch = props.dispatch;
      var undoManager = yUndoPluginKey.getState(state).undoManager;

      if (undoManager.redoStack.length === 0) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      return convertCommand(redo)(props);
    });
  }
  /**
   * Handle the undo keybinding.
   */


  undoShortcut(props) {
    return this.yUndo()(props);
  }
  /**
   * Handle the redo keybinding for the editor.
   */


  redoShortcut(props) {
    return this.yRedo()(props);
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "yUndo", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "yUndo"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "yRedo", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "yRedo"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "undoShortcut", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "undoShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "redoShortcut", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "redoShortcut"), _class2.prototype)), _class2)) || _class);
/**
 * The default destroy provider method.
 */

function defaultDestroyProvider(provider) {
  var doc = provider.doc;
  provider.disconnect();
  provider.destroy();
  doc.destroy();
}

function getLazyValue(lazyValue) {
  return isFunction(lazyValue) ? lazyValue() : lazyValue;
}

export { YjsExtension, defaultDestroyProvider };
